/**
 * Renormalisation d'un champ numerique a la sortie du champ.
 *
 * A brancher sur `(committed)` d'`app-input`, que le kit emet au blur et au clear —
 * « l'utilisateur a fini d'editer ». Pas sur `valueChanges` : reecrire pendant la
 * frappe empeche de taper `10,` avant la decimale.
 *
 * Ici et non dans `partage/format/` : ces fonctions manipulent un `FormControl`,
 * donc dependent d'Angular Forms, la ou `format/` est du formatage pur sans
 * dependance. Le calcul de la chaine, lui, reste dans `format/nombre.ts`.
 *
 * ⚠️ Le resultat est REECRIT dans le controle : c'est donc lui qui part au serveur.
 * D'ou deux fonctions distinctes plutot qu'un plafond unique — arrondir un taux
 * modifierait la donnee soumise (`2,375` devenu `2,38`, regression attrapee en
 * revue), alors que borner un montant a deux decimales EST la regle metier.
 *
 * @module partage/forms
 */
import { saisieNormalisee } from '../format/nombre';

import type { AbstractControl } from '@angular/forms';

/**
 * Deux decimales : la precision d'un montant en euros. Regle metier, pas confort
 * d'affichage — la colonne `MontantAcompte` est declaree `HasPrecision(18, 2)`, donc
 * une troisieme decimale saisie est arrondie a l'enregistrement de toute facon. La
 * borner ici ne perd rien : elle rend juste visible ce que la base fera.
 */
const DECIMALES_MONTANT = 2;

/*
 * `AbstractControl` et non `FormControl<number>` : le kit pousse une CHAINE dans des
 * controles declares `number` (voir l'en-tete de `format/nombre.ts`), et leur type
 * varie d'un ecran a l'autre. Le `value` non type d'`AbstractControl` dit la verite
 * de ce couplage, la ou un `FormControl<number | null>` aurait impose un `as never` a
 * chaque ecriture.
 *
 * L'evenement est emis volontairement : les miroirs `toSignal` qui pilotent les
 * lignes d'erreur, et les validateurs de plafond, doivent voir la valeur normalisee.
 */
function reecrire(controle: AbstractControl, normalisee: string): void {
  if (normalisee !== String(controle.value ?? '')) {
    controle.setValue(normalisee);
  }
}

/**
 * Canonise la saisie SANS rien perdre : `010` → `10`, `5.50` → `5,5`, `2.375` inchange.
 * Pour les taux, les quantites — tout ce dont on n'a pas le droit de rogner la precision.
 */
export function normaliserSaisieNombre(controle: AbstractControl): void {
  reecrire(controle, saisieNormalisee(controle.value));
}

/** Canonise et borne a deux decimales : `010` → `10`, `1,234` → `1,23`. Pour les euros. */
export function normaliserSaisieMontant(controle: AbstractControl): void {
  reecrire(controle, saisieNormalisee(controle.value, DECIMALES_MONTANT));
}
